<template>
  <v-card
    variant="outlined"
    class="cursor-pointer draggable-card"
    :draggable="true"
    :data-testid="`yard-asset-visit-${assetVisit.id}`"
    :id="assetVisit.id"
    :class="{ dragging: isDragging }"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
    @drop="onDrop"
    @dragover.prevent>
    <v-card-text>
      <div class="d-flex flex-column gap-3">
        <div class="d-flex flex-row justify-start align-center gap-2 pl-9" style="height: 30px">
          <v-icon style="position: absolute; left: 10px">mdi-drag-vertical</v-icon>
          <div class="ml-8">
            <yard-view-truck
              v-if="!isAssetContainer(assetVisit)"
              :rotation="270"
              :asset-visit="assetVisit" />
            <svg-asset-container v-else :rotation="270" />
          </div>
        </div>

        <div class="d-flex flex-column gap-1">
          <div class="mb-2" v-if="!isAssetContainer(assetVisit)">
            <p class="typography-body-md-bold mb-1">
              {{ getTranslatedCompanyName(assetVisit.company?.name || assetVisit.companyHint) }}
            </p>
            <v-chip size="x-small" variant="tonal">
              <strong>{{ getAssetVisitTypeDisplayName(assetVisit.visitType) }}</strong>
            </v-chip>
          </div>
          <div v-else>
            <p class="typography-body-md-bold">
              {{ assetVisit.code }}
            </p>
            <v-chip size="x-small" variant="tonal" class="text-capitalize">
              <strong>{{ assetVisitType }}</strong>
            </v-chip>
          </div>

          <div class="d-flex flex-column typography-body-sm text--color-text-tertiary">
            <span class="typography-body-sm-bold mr-1">
              <v-icon size="small">mdi-map-marker-outline</v-icon>
              Arrived:
            </span>
            {{ arrivedTimeFormatted }}
          </div>

          <div
            class="typography-body-sm text--color-text-tertiary"
            v-if="isAssetContainer(assetVisit) && assetVisit.pickupAppointment?.id">
            <span class="typography-body-sm-bold mr-1">Carrier pickup location:</span>
            <p>
              {{ assetContainerPickupSpotLocation || t('yard.common.phrases.notArrived') }}
            </p>
          </div>

          <div
            v-if="appointment?.refNumber"
            class="d-flex flex-column align-start typography-body-sm text--color-text-tertiary">
            <span class="typography-body-sm-bold">
              {{
                getTranslatedRefNumberDisplayName($refNumSettings(selectedWarehouse).displayName)
              }}:
            </span>
            <p>
              {{ appointment.refNumber }}
            </p>
          </div>
          <div
            v-else-if="appointment?.confirmationNumber"
            class="d-flex flex-column align-start typography-body-sm text--color-text-tertiary">
            <span class="typography-body-sm-bold">{{ t('yard.headers.confirmationNumber') }}:</span>
            <p>
              {{ appointment.confirmationNumber }}
            </p>
          </div>

          <div
            v-if="appointment?.dock?.name"
            class="d-flex flex-column align-start typography-body-sm text--color-text-tertiary">
            <span class="typography-body-sm-bold">{{ t('yard.headers.plannedDock') }}:</span>
            <p>
              {{ appointment.dock.name }}
            </p>
          </div>
        </div>
        <v-btn
          size="small"
          variant="outlined"
          block
          @click="selectedAssetVisit = assetVisit"
          :data-testid="`yard-asset-visit-view-more-${assetVisit.id}`">
          {{ t('yard.common.phrases.viewMore') }}
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { computed, defineComponent, ref } from 'vue';
import { isUUID } from 'class-validator';

import useYard from '@/modules/yard/composables/useYard';
import {
  LuxonDateTimeFormats,
  AssetVisitType,
  getTranslatedRefNumberDisplayName
} from '@nova/core';
import { useEventHub, useNotify } from '@/composables';
import YardViewTruck from '../YardViewTruck.vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewAssetVisit',
  methods: { getTranslatedRefNumberDisplayName },
  components: { YardViewTruck },
  props: {
    assetVisit: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const { t } = useI18n();
    const yardStore = useYard();

    const isDragging = ref(false);
    const originalElement = ref(null);
    const eventHub = useEventHub();
    const notify = useNotify();

    const arrivedTimeFormatted = computed(() => {
      return yardStore.formatDateTime(
        props.assetVisit?.createDateTime,
        LuxonDateTimeFormats.ShortDayDateSlashed12Hour,
        LuxonDateTimeFormats.ShortDayDateSlashed24Hour
      );
    });

    const appointment = computed(() => props.assetVisit.appointment);
    const assetVisitType = computed(
      () => yardStore.getAssetVisitTypeDisplayName(props.assetVisit.type) || props.assetVisit.type
    );

    const onDragStart = event => {
      isDragging.value = true;
      originalElement.value = event.target;

      // Add the dragging class to change appearance
      event.target.classList.add('dragging');

      // Set the data for transfer
      event.dataTransfer.setData('text/plain', event.target.id);

      setTimeout(() => {
        event.target.style.display = 'none';
        event.target.classList.add('dragging');
      }, 0);
    };

    const onDragEnd = event => {
      isDragging.value = false;

      // Revert the element's appearance
      originalElement.value.style.display = 'block';

      // Get the element and remove the dragging class
      const id = event.dataTransfer.getData('text/plain');
      const element = document.getElementById(id);

      if (element) {
        element.classList.remove('dragging');
      }

      // Check if the element was dropped inside a drop-zone
      const dropZone = document
        .elementFromPoint(event.clientX, event.clientY)
        .closest('.drop-zone');

      if (isUUID(dropZone?.id)) {
        const spot = { ...yardStore.spotsRef.value.find(spot => spot.id === dropZone.id) };

        if (spot && yardStore.isSpotOpen(spot)) {
          return yardStore
            .createSpotAssignment({
              spotId: dropZone.id,
              [yardStore.isAssetContainer(props.assetVisit) ? 'assetContainerId' : 'assetVisitId']:
                props.assetVisit.id
            })
            .then(spotAssignment => {
              eventHub.$emit(yardStore.SPOT_ASSIGNMENT_CHANGED, {
                assetVisit: props.assetVisit,
                newSpotCode: spotAssignment.spot.code,
                newSpotAreaName: spotAssignment.spot.spotArea.name
              });
              yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.ASSIGNED, {
                spot,
                assetVisit: props.assetVisit,
                entryPoint: 'Drag and Drop'
              });
            });
        }

        // This is the case when there is something already on the spot
        // And can be combined
        if (spot && !yardStore.isSpotClosed(spot)) {
          const isAssetContainerAssignedToVisitOnly =
            yardStore.isAssetContainer(props.assetVisit) &&
            yardStore.hasAssetVisitOnlyOnSpot(spot) &&
            spot.spotAssignments[0].assetVisit.visitType !== AssetVisitType.Live;

          const isVisitAssignedToContainerOnly =
            yardStore.hasAssetContainerOnlyOnSpot(spot) &&
            !yardStore.isAssetContainer(props.assetVisit) &&
            props.assetVisit.visitType !== AssetVisitType.Live;

          if (isAssetContainerAssignedToVisitOnly || isVisitAssignedToContainerOnly) {
            return yardStore
              .updateSpotAssignment(spot.spotAssignments[0].id, {
                spotId: spot.id,
                [yardStore.isAssetContainer(props.assetVisit)
                  ? 'assetContainerId'
                  : 'assetVisitId']: props.assetVisit.id
              })
              .then(() => {
                eventHub.$emit(yardStore.SPOT_ASSIGNMENT_CHANGED, {
                  assetVisit: props.assetVisit,
                  newSpotCode: spot.code,
                  newSpotAreaName: yardStore.spotAreasRef.value.find(
                    area => area.id === spot.spotAreaId
                  )?.name
                });

                yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.ASSIGNED, {
                  spot,
                  assetVisit: props.assetVisit,
                  entryPoint: 'Drag and Drop'
                });
              });
          }
        }

        notify(t('yard.messages.invalidDestinationTryAgainToAnotherSpot'), 'warning');
        return;
      }
      originalElement.value.style.display = 'block';
    };

    /*
     * This happens only when dropping somewhere off the dom
     */
    const onDrop = event => {
      event.preventDefault();

      const elementId = event.dataTransfer.getData('text/plain');
      const element = document.getElementById(elementId);

      if (element) {
        element.classList.remove('dragging');
      }
    };

    const assetContainerPickupSpot = computed(() => {
      const spot = yardStore.getAppointmentSpot(props.assetVisit.pickupAppointmentId);

      return spot ?? null;
    });

    const assetContainerPickupSpotLocation = computed(() => {
      if (!assetContainerPickupSpot.value) {
        return null;
      }

      return `${assetContainerPickupSpot.value.spotArea.name} - ${assetContainerPickupSpot.value.code}`;
    });

    return {
      t,
      selectedWarehouse: yardStore.selectedWarehouseRef,
      isAssetContainer: yardStore.isAssetContainer,
      assetContainerPickupSpotLocation,
      selectedAssetVisit: yardStore.selectedAssetVisitRef,
      getAssetVisitTypeDisplayName: yardStore.getAssetVisitTypeDisplayName,
      appointment,
      onDragStart,
      arrivedTimeFormatted,
      onDragEnd,
      onDrop,
      isDragging,
      assetVisitType,
      getTranslatedCompanyName: yardStore.getTranslatedCompanyName
    };
  }
});
</script>
<style lang="scss" scoped>
.draggable-card {
  transition: 0.3s ease;

  &:hover {
    background-color: $color-neutral-20;
    transform: scale(1.05);
    /* Slightly enlarge the card */
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    /* Add a shadow */
  }
}

/* Styles to apply while the card is being dragged */
.dragging {
  opacity: 0.6 !important;
  /* Make the card semi-transparent */
  border: 2px dashed $color-primary-60 !important;
  /* Change border to indicate dragging */
  background-color: $color-neutral-10 !important;
  /* Optional background change */
}

.v-card {
  padding: $spacing-4;

  border-radius: 8px !important;
  border-color: $color-line-divider !important;
  background-color: $color-background-secondary;
}

:deep(.v-card-item),
.v-card-text {
  padding: 0;
}
</style>
